import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePackContext } from "../../context/pack";
import { gql, useQuery } from "@apollo/client";
import Header from "../../../../components/common/header";
import clsx from "clsx";
import LoadingSpinnerFull from "../../../../components/common/loading-spinner/loading-spinner-full";
import ResponsiveCDNImage from "../../../../components/common/image/responsive-cdn-image";
import BottomSheet from "../../../../components/common/bottom-sheet";
import { notify } from "../../../../utils/toast";
import { Menu } from "../../../../__generated__/graphql";
import { track } from "@amplitude/analytics-browser";
export const GET_MENUS_BY_STORE = gql`
  query GetMenusByStore($storeId: Int!) {
    menus(storeId: $storeId) {
      id
      name
      price
      img
    }
  }
`;
interface MenuItem {
  id: number;
  name: string;
  img: string;
  price: number;
  quantity: number; // 수량 필드 추가
  discountRate: number;
}

const SelectFromMenu: React.FC = () => {
  const navigate = useNavigate();
  const { addPack } = usePackContext();
  const [selectedItems, setSelectedItems] = useState<MenuItem[]>([]);
  const [discountRate, setDiscountRate] = useState(50);
  const storeId = 1; // 실제 storeId는 동적으로 가져오거나 context에서 가져오는 방식으로 수정
  const [isSampleMenu, setIsSampleMenu] = useState<boolean>(false); // 샘플 메뉴 상태 초기값을 true로 설정
  const [allUploadDiscountRateModal, setAllUploadDiscountRateModal] =
    useState(false);
  const { loading, error, data } = useQuery(GET_MENUS_BY_STORE, {
    variables: { storeId },
  });

  const handleSelect = (menu: Omit<MenuItem, "quantity">) => {
    setSelectedItems(
      (prev) =>
        prev.some((item) => item?.id === menu?.id)
          ? prev.filter((item) => item?.id !== menu?.id)
          : [...prev, { ...menu, quantity: 1 }] // 기본 수량 1
    );
  };

  const handleComplete = () => {
    if (selectedItems.length === 0) {
      alert("메뉴를 선택해주세요!");
      return;
    }
    if (selectedItems.length > 1) {
      track("묶어서 만들기 버튼 클릭", {
        어디서: "실시간업로드",
      });
    }

    const packWithDiscount = selectedItems.map((item) => ({
      ...item,
      discountRate,
    }));
    addPack(packWithDiscount, isSampleMenu, undefined, 1);
    navigate("/boss/live-order/upload");
  };

  const handleAllUpload = () => {
    setAllUploadDiscountRateModal(true);
  };

  const handleAllUploadConfirm = async () => {
    setAllUploadDiscountRateModal(false);

    const menus = Object.values(data?.menus || {});

    // 모든 메뉴를 하나씩 팩으로 만들기
    menus.forEach((menu) => {
      if (!menu) return;

      const typedMenu = menu as Menu;
      const packWithDiscount: MenuItem[] = [
        {
          id: typedMenu.id,
          name: typedMenu.name,
          img: typedMenu.img || "",
          price: typedMenu.price,
          discountRate,
          quantity: 1,
        },
      ];

      addPack(packWithDiscount, isSampleMenu, undefined, 1);
    });

    navigate("/boss/live-order/upload");
    notify("완료하기를 눌러 게시해주세요!");
  };

  const handleEachUpload = async () => {
    if (selectedItems.length === 0) {
      alert("메뉴를 선택해주세요!");
      return;
    }
    track("따로 만들기 버튼 클릭", {
      어디서: "실시간업로드",
    });
    // 선택된 메뉴들을 각각 개별 팩으로 만들기
    selectedItems.forEach((item) => {
      const packWithDiscount: MenuItem[] = [
        {
          ...item,
          discountRate,
        },
      ];

      addPack(packWithDiscount, isSampleMenu, undefined, item.quantity);
    });

    navigate("/boss/live-order/upload");
  };

  if (loading) return <LoadingSpinnerFull />;
  if (error) return <p>Error: {error.message}</p>;

  const menus: Omit<MenuItem, "quantity">[] = data?.menus || [];

  return (
    <div className="h-screen bg-white overflow-y-scroll">
      <Header title="팩 구성" showBackButton>
        <Header.RightContent>
          <button
            className="text-blue-500 underline font-bold"
            onClick={() => {
              navigate("/boss/live-order/manual-entry");
            }}
          >
            직접입력
          </button>
        </Header.RightContent>
      </Header>

      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6 flex justify-between items-center">
          메뉴에서 선택
          <button
            className="text-blue-500 font-bold text-md border px-1.5 py-1"
            onClick={handleAllUpload}
          >
            모든 메뉴 하나씩 팩 만들기
          </button>
        </h2>

        {menus.length === 0 ? (
          <div>
            <div
              className="w-1/2 flex flex-col justify-center items-center h-full bg-white border-2 border-dashed border-gray-300 rounded-lg py-4"
              onClick={() => navigate("/boss/live-order/menu-management")}
            >
              <div className="text-4xl pb-1 font-bold text-white rounded-full bg-green-500 h-10 w-10 flex items-center justify-center">
                +
              </div>
              <div className="text-3xl text-gray-500">메뉴 추가</div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {menus.map((menu) => (
              <div
                key={menu?.id}
                className={` border rounded-lg cursor-pointer ${
                  selectedItems.some((item) => item?.id === menu?.id)
                    ? "bg-blue-400 text-white"
                    : "bg-[#f3f3f3]"
                }`}
              >
                <div onClick={() => handleSelect(menu)}>
                  <ResponsiveCDNImage
                    src={menu?.img}
                    alt={menu?.name}
                    className="w-full h-28 object-cover rounded-md mb-2"
                    sizeRatio={1 / 3}
                  />
                  <div className="p-2">
                    <div className="text-2xl font-semibold">{menu?.name}</div>
                    <div className="text-xl text-gray-800">
                      {menu?.price.toLocaleString()}원
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div
              className="col-span-1 flex flex-col justify-center items-center w-full h-full bg-white border-2 border-dashed border-gray-300 rounded-lg"
              onClick={() => navigate("/boss/live-order/menu-management")}
            >
              <div className="text-4xl pb-1 font-bold text-white rounded-full bg-green-500 h-10 w-10 flex items-center justify-center">
                +
              </div>
              <div className="text-3xl text-gray-500">메뉴 추가</div>
            </div>
          </div>
        )}
        <p className="text-gray-500 mt-2">* 수량설정은 다음 페이지에서</p>

        <div className="mt-10">
          <label className="block text-2xl font-bold mb-2">
            할인율 적용 (%)
          </label>
          <div className="text-gray-500 mb-2">
            마감히어로 데이터를 분석한 결과, 50% 할인율이 가장 높은 판매율을
            보였습니다.
            <span style={{ color: "green", marginLeft: 4 }}>50%</span>를
            권장합니다!
          </div>
          <div className="flex space-x-4">
            {[30, 40, 50, 60].map((rate) => (
              <button
                key={rate}
                onClick={() => setDiscountRate(rate)}
                type="button"
                className={clsx("px-4 py-2 border rounded-md", {
                  "bg-blue-500 text-white": discountRate === rate,
                  "bg-gray-200": discountRate !== rate,
                })}
              >
                {rate}%
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10">
          <label className="block text-2xl font-bold mb-2">상품 유형</label>
          <div className="text-gray-500 mb-2">
            소비자에게 표시됩니다! 마감상품이 아닌데 마감상품으로 생각하면
            아쉽잖아요 😁
          </div>
          <div className="flex flex-col items-start space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!isSampleMenu}
                onChange={(e) => setIsSampleMenu(!e.target.checked)}
                className="w-5 h-5"
              />
              마감상품
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isSampleMenu}
                onChange={(e) => setIsSampleMenu(e.target.checked)}
                className="w-5 h-5"
              />
              신상품 (홍보용, 신메뉴, 프로모션, 맛보기 등)
            </label>
          </div>
        </div>

        {selectedItems.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-2">선택된 메뉴</h3>
            <div className="flex flex-wrap gap-2">
              {selectedItems.map((menu) => (
                <div key={menu?.id} className="flex items-center space-x-4">
                  <ResponsiveCDNImage
                    src={menu?.img}
                    alt={menu?.name}
                    className="w-16 h-16 object-cover rounded-md"
                    sizeRatio={1 / 6}
                  />
                  <div>
                    <div className="text-black text-lg">{menu?.name}</div>
                    <div className="text-md text-gray-500">
                      {menu?.price.toLocaleString()}원 (수량: {menu?.quantity})
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8">
          {selectedItems.length >= 2 && (
            <div className="flex gap-4">
              <button
                onClick={handleEachUpload}
                className="w-2/5 py-4 bg-green-500 text-white rounded-lg active:bg-gray-600 transition duration-150"
              >
                따로 만들기
                <br />({selectedItems.length}개 업로드)
              </button>
              <button
                onClick={handleComplete}
                className={`${
                  selectedItems.length >= 2 ? "w-3/5" : "w-full"
                } py-4 bg-[#1562fc] text-white rounded-lg active:bg-[#124ab7] transition duration-150`}
              >
                묶어서 만들기
                <br />
                (1개 업로드)
              </button>
            </div>
          )}
          {selectedItems.length < 2 && (
            <div className="flex gap-4">
              <button
                onClick={handleComplete}
                className={
                  "w-full py-4 bg-[#1562fc] text-white rounded-lg active:bg-[#124ab7] transition duration-150"
                }
              >
                다음
              </button>
            </div>
          )}
        </div>
        <BottomSheet
          isOpen={allUploadDiscountRateModal}
          onClose={() => setAllUploadDiscountRateModal(false)}
        >
          <div className="p-2">
            <div>
              <label className="block text-lg font-semibold mb-2">
                할인율 적용 (%)
              </label>
              <p className="text-gray-800 mb-2">
                할인율을 선택해주세요! 선택하신 할인율로 일괄 적용돼요!
              </p>
              <div className="flex space-x-4">
                {[30, 40, 50, 60].map((rate) => (
                  <button
                    key={rate}
                    onClick={() => setDiscountRate(rate)}
                    type="button"
                    className={clsx("px-4 py-2 border rounded-md", {
                      "bg-blue-500 text-white": discountRate === rate,
                      "bg-gray-200": discountRate !== rate,
                    })}
                  >
                    {rate}%
                  </button>
                ))}
              </div>
            </div>

            <div className="my-6">
              <label className="block text-lg font-semibold mb-2">
                상품 유형
              </label>
              <div className="text-gray-800 mb-2">
                소비자에게 표시됩니다! 마감상품이 아닌데 마감상품으로 생각하면
                아쉽잖아요 😁
              </div>
              <div className="flex flex-col items-start space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!isSampleMenu}
                    onChange={(e) => setIsSampleMenu(!e.target.checked)}
                    className="w-5 h-5"
                  />
                  마감상품
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isSampleMenu}
                    onChange={(e) => setIsSampleMenu(e.target.checked)}
                    className="w-5 h-5"
                  />
                  신상품 (홍보용, 신메뉴, 프로모션, 맛보기 등)
                </label>
              </div>
            </div>

            <button
              onClick={handleAllUploadConfirm}
              className="w-full py-3 bg-[#1562fc] text-white rounded-md active:bg-[#124ab7] transition duration-150"
            >
              적용하기
            </button>
          </div>
        </BottomSheet>
      </div>
    </div>
  );
};

export default SelectFromMenu;
